import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { X, History, Loader2 } from 'lucide-react';
import api from '../../../../api/client';

const PAGE_SIZE = 10;

const UserLoginActivityModal = ({ open, onClose, userId, userName, companyId }) => {
  const { t } = useTranslation();
  const [list, setList] = useState([]);
  const [totalCount, setTotalCount] = useState(0);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState(null);

  const hasMore = list.length < totalCount;

  const fetchPage = async (pageNum, append = false) => {
    if (!companyId || !userId) return;
    const isFirst = pageNum === 1;
    if (isFirst) setLoading(true);
    else setLoadingMore(true);
    setError(null);
    try {
      const res = await api.get(
        `/companies/${companyId}/users/login_activity/`,
        { params: { user_id: userId, page: pageNum, page_size: PAGE_SIZE } }
      );
      const results = res.data?.results ?? [];
      const count = res.data?.count ?? 0;
      if (append) {
        setList((prev) => [...prev, ...results]);
      } else {
        setList(results);
      }
      setTotalCount(count);
    } catch (err) {
      console.error('Error fetching login activity:', err);
      const msg =
        err.response?.data?.error ||
        err.response?.data?.detail ||
        t('companyAdmins.loginActivityError');
      setError(msg);
      if (!append) setList([]);
    } finally {
      if (isFirst) setLoading(false);
      else setLoadingMore(false);
    }
  };

  useEffect(() => {
    if (open && companyId && userId) {
      setPage(1);
      setList([]);
      setTotalCount(0);
      setError(null);
      fetchPage(1, false);
    }
  }, [open, companyId, userId]);

  const handleLoadMore = () => {
    const nextPage = page + 1;
    setPage(nextPage);
    fetchPage(nextPage, true);
  };

  const handleClose = () => {
    onClose();
  };

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50">
      <div className="bg-white rounded-2xl shadow-xl max-w-2xl w-full max-h-[85vh] flex flex-col">
        <div className="flex items-center justify-between p-4 border-b border-gray-200">
          <h2 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
            <History size={20} />
            {t('companyAdmins.loginActivityModalTitle', { name: userName || '' })}
          </h2>
          <button
            type="button"
            onClick={handleClose}
            className="p-2 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-lg transition-colors"
            aria-label={t('common.cancel')}
          >
            <X size={20} />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto p-4">
          {loading ? (
            <div className="flex items-center justify-center py-12 text-gray-500">
              <Loader2 size={24} className="animate-spin mr-2" />
              {t('profile.loginActivityLoading')}
            </div>
          ) : error ? (
            <p className="text-red-600 py-6 text-center">{error}</p>
          ) : list.length === 0 ? (
            <p className="text-gray-500 py-8 text-center">
              {t('companyAdmins.loginActivityEmpty')}
            </p>
          ) : (
            <>
              <div className="overflow-x-auto rounded-lg border border-gray-200">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-600 uppercase tracking-wider">
                        {t('profile.loginDatetime')}
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-600 uppercase tracking-wider">
                        {t('profile.ipAddress')}
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-600 uppercase tracking-wider">
                        {t('profile.location')}
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {list.map((row, idx) => (
                      <tr key={idx} className="hover:bg-gray-50">
                        <td className="px-4 py-3 text-sm text-gray-900 whitespace-nowrap">
                          {row.login_datetime
                            ? new Date(row.login_datetime).toLocaleString()
                            : '—'}
                        </td>
                        <td className="px-4 py-3 text-sm text-gray-600">
                          {row.ip_address || '—'}
                        </td>
                        <td className="px-4 py-3 text-sm text-gray-600">
                          {row.location
                            ? typeof row.location === 'string'
                              ? row.location
                              : row.location?.formatted_address ||
                                JSON.stringify(row.location)
                            : '—'}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <div className="mt-4 flex justify-center">
                {hasMore ? (
                  <button
                    type="button"
                    onClick={handleLoadMore}
                    disabled={loadingMore}
                    className="inline-flex items-center gap-2 px-4 py-2 text-sm font-medium text-blue-600 hover:text-blue-800 bg-blue-50 hover:bg-blue-100 rounded-lg transition-colors disabled:opacity-50"
                  >
                    {loadingMore ? (
                      <Loader2 size={16} className="animate-spin" />
                    ) : null}
                    {t('companyAdmins.loginActivityLoadMore')}
                  </button>
                ) : (
                  <span className="text-sm text-gray-500">
                    {t('companyAdmins.loginActivityShowing', {
                      count: list.length,
                      total: totalCount
                    })}
                  </span>
                )}
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default UserLoginActivityModal;
